/**
 * DocumentList Component
 * 
 * Renders a browsable list of folders and documents from an S3 bucket path.
 * Users can navigate through folders and download files.
 * 
 * @example
 * // Render as inline component (default)
 * <DocumentList 
 *   path="employees/4321/1000/" 
 *   credentials={credentials}
 *   title="Employee Documents"
 * />
 * 
 * @example
 * // Render as clickable link that opens in modal
 * <DocumentList 
 *   path="employees/4321/1000/" 
 *   credentials={credentials}
 *   linkText="View Documents"
 *   title="Employee Documents"
 * />
 * 
 * @example
 * // Custom link text and modal title
 * <DocumentList 
 *   path="invoices/12345/" 
 *   credentials={credentials}
 *   linkText="📁 Open Invoice Documents"
 *   title="Invoice Documents"
 *   baseFolderLabel="Invoices"
 * />
 * 
 * @param {Object} props - Component props
 * @param {string} [props.path] - S3 bucket folder path (e.g., "employees/4321/1000/"). Empty string for root.
 * @param {Object} props.credentials - User credentials object with accessToken
 * @param {string} [props.title] - Optional title to display above the document list (also used as modal title)
 * @param {string} [props.baseFolderLabel] - Label for the base folder in breadcrumbs (default: "Documents")
 * @param {string} [props.linkText] - If provided, component renders as a clickable link that opens modal. If not provided, renders inline.
 * @param {string} [props.documentPermission] - Document permission scope (employee, vendor, invoice, candidate). Derived from path if omitted.
 * @param {Function} [props.onPathChange] - Optional callback when path changes (receives new path as argument)
 */
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { CircularProgress, Breadcrumbs, Link, Typography, Dialog, DialogTitle, DialogContent, IconButton } from '@material-ui/core'
import FolderIcon from '@material-ui/icons/Folder'
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile'
import NavigateNextIcon from '@material-ui/icons/NavigateNext'
import CloseIcon from '@material-ui/icons/Close'
import ReactTable from './ReactTable'
import { dateFormat } from '../utils/formatter'
import { resolveEnabledModules, isModuleEnabled } from '../config/permissions-registry'

const api = process.env.REACT_APP_API

const getDocumentPermissionFromPath = (path) => {
  const rootFolder = (path || '').split('/').filter(Boolean)[0]
  if (rootFolder === 'employees') return 'employee'
  if (rootFolder === 'vendors') return 'vendor'
  if (rootFolder === 'invoices') return 'invoice'
  if (rootFolder === 'candidates') return 'candidate'
  return null
}

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}

// Column definitions for the document list table
const getDocumentListCols = (onFolderClick) => [
  {
    accessorKey: 'name',
    header: 'Name',
    enableSorting: true,
    enableColumnFilter: true,
    size: 350,
    Cell: ({ cell, row }) => {
      const item = row.original
      if (item.type === 'folder') {
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              color: '#1976d2',
              fontWeight: 500
            }}
            onClick={(e) => {
              e.stopPropagation()
              onFolderClick(item.path)
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.textDecoration = 'underline'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = 'none'
            }}
          >
            <FolderIcon style={{ marginRight: '8px', fontSize: '20px', color: '#1976d2' }} />
            <span>{item.name}</span>
          </div>
        )
      }
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <InsertDriveFileIcon style={{ marginRight: '8px', fontSize: '20px', color: '#666' }} />
          <span>{item.name}</span>
        </div>
      )
    }
  },
  {
    accessorKey: 'type',
    header: 'Type',
    enableSorting: true,
    enableColumnFilter: true,
    size: 100,
    Cell: ({ row }) => {
      const item = row.original
      return (
        <span style={{ 
          textTransform: 'capitalize',
        }}>
          {item.type}
        </span>
      )
    }
  },
  {
    accessorKey: 'size',
    header: 'Size',
    enableSorting: true,
    enableColumnFilter: false,
    size: 100,
    Cell: ({ cell, row }) => {
      // Don't show size for folders
      return row.original.type === 'folder' ? '-' : cell.getValue()
    }
  },
  {
    accessorKey: 'lastModified',
    header: 'Last Modified',
    enableSorting: true,
    enableColumnFilter: false,
    size: 150,
    Cell: ({ cell, row }) => {
      // Don't show lastModified for folders
      if (row.original.type === 'folder') return '-'
      const value = cell.getValue()
      return value ? dateFormat(value) : '-'
    }
  },
  {
    accessorKey: 'download',
    header: 'Action',
    enableSorting: false,
    enableColumnFilter: false,
    size: 120,
    Cell: ({ row }) => {
      const item = row.original
      // Only show download for files
      if (item.type === 'folder') return '-'
      return row.original.download || null
    }
  }
]

export default function DocumentList (props) {
  const { path: initialPath, credentials, title, baseFolderLabel, linkText, documentPermission, onPathChange } = props
  // Use baseFolderLabel if provided, otherwise use title, otherwise default to 'Documents'
  const baseLabel = baseFolderLabel || title || 'Documents'
  const [basePath, setBasePath] = useState(initialPath || '') // The minimum allowed path (starting point)
  const [currentPath, setCurrentPath] = useState(initialPath || '')
  const [folders, setFolders] = useState([])
  const [files, setFiles] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [downloadingId, setDownloadingId] = useState(null)
  const [error, setError] = useState(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [documentCount, setDocumentCount] = useState(null) // { folders: 0, files: 0 } or null if not checked yet
  const [isCheckingCount, setIsCheckingCount] = useState(false)
  
  // Check if DOCUMENTS module is enabled for this client
  const rawModules = credentials?.clientModules
  const enabledModules = resolveEnabledModules(rawModules)
  const isDocumentsModuleEnabled = isModuleEnabled('DOCUMENTS', enabledModules)
  const permissionScope = documentPermission || getDocumentPermissionFromPath(initialPath)
  const allow = credentials?.allow || (() => false)
  const hasDocumentPermission = permissionScope
    ? allow('subpage', 'DOCUMENTS', permissionScope)
    : false
  

  console.log('props', props)
  // Use refs to store callback and credentials to prevent unnecessary re-fetches
  const onPathChangeRef = useRef(onPathChange)
  const credentialsRef = useRef(credentials)
  
  // Update refs when props change (without triggering re-renders)
  useEffect(() => {
    onPathChangeRef.current = onPathChange
  }, [onPathChange])
  
  useEffect(() => {
    credentialsRef.current = credentials
  }, [credentials])

  // Initialize base path and current path when component mounts or initialPath changes
  useEffect(() => {
    if (initialPath !== undefined) {
      const normalizedPath = initialPath || ''
      setBasePath(normalizedPath)
      setCurrentPath(normalizedPath)
    }
  }, [initialPath])

  // Reset to base path when dialog closes (only if linkText is provided)
  useEffect(() => {
    if (linkText && !dialogOpen) {
      // When dialog closes, reset current path to base path
      setCurrentPath(basePath || initialPath || '')
    }
  }, [dialogOpen, linkText, basePath, initialPath])

  // Helper function to check if a path is at or below the base path
  const isPathAllowed = (path) => {
    if (!basePath) return true // If no base path, allow all navigation (root access)
    
    // Normalize paths: ensure they end with / for comparison
    const normalizedBase = basePath.endsWith('/') ? basePath : basePath + '/'
    const normalizedPath = path ? (path.endsWith('/') ? path : path + '/') : ''
    
    // Allow going back to base path (exact match)
    if (normalizedPath === normalizedBase) {
      return true
    }
    
    // Allow empty path if it means going back to base
    if (!path || path === '') {
      return true
    }
    
    // Path is allowed if it starts with the base path (is deeper)
    return normalizedPath.startsWith(normalizedBase)
  }

  const fetchFiles = useCallback(async () => {
    if (!hasDocumentPermission) {
      setFolders([])
      setFiles([])
      return
    }

    // Allow empty path for root listing
    setIsLoading(true)
    setError(null)

    try {
      // Use refs to get current values without including them in dependencies
      const currentCredentials = credentialsRef.current
      const currentAccessToken = currentCredentials?.user?.accessToken || ''
      
      // Use direct fetch for GET request with query parameters
      // Empty path is valid - it means root of bucket
      // Access check is now handled server-side by the /list-attachments endpoint
      const apiUrl = `${api}/list-attachments?path=${encodeURIComponent(currentPath || '')}`
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentAccessToken}`
        }
      })

      if (!response.ok) {
        const errorData = await response.json()
        // Handle 403 Forbidden (access denied) specifically
        if (response.status === 401) {
            props.credentials.login() 
            throw new Error('Your access token has expired. Please log in again.')
        } else if (response.status === 403) {
          setError(errorData.error || 'You do not have permission to access documents for this employee.')
        } else {
          setError(errorData.error || `Error: ${response.status}`)
        }
        setFolders([])
        setFiles([])
        return
      }

      const result = await response.json()

      if (result.success && result.data) {
        const foldersList = result.data.folders || []
        const filesList = result.data.files || []
                
        setFolders(foldersList)
        setFiles(filesList)
        
        // Notify parent component of path change if callback provided (using ref)
        if (onPathChangeRef.current) {
          onPathChangeRef.current(currentPath)
        }
      } else {
        setFolders([])
        setFiles([])
      }
    } catch (e) {
      console.error('Error fetching files:', e)
      setError(e.message || 'Failed to load documents')
      setFolders([])
      setFiles([])
    } finally {
      setIsLoading(false)
    }
  }, [currentPath, hasDocumentPermission]) // Credentials are accessed via refs

  // Check document count without opening the dialog (only if linkText is provided)
  const checkDocumentCount = useCallback(async () => {
    if (!hasDocumentPermission) {
      setDocumentCount(null)
      return
    }

    if (!initialPath) {
      setDocumentCount({ folders: 0, files: 0 })
      return
    }

    setIsCheckingCount(true)
    try {
      const currentCredentials = credentialsRef.current
      const currentAccessToken = currentCredentials?.user?.accessToken || ''
      
      const apiUrl = `${api}/list-attachments?path=${encodeURIComponent(initialPath || '')}`
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentAccessToken}`
        }
      })

      if (response.ok) {
        const result = await response.json()
        if (result.success && result.data) {
          const foldersCount = result.data.folders?.length || 0
          const filesCount = result.data.files?.length || 0
          setDocumentCount({ folders: foldersCount, files: filesCount })
        } else {
          setDocumentCount({ folders: 0, files: 0 })
        }
      } else {
        // If access denied or error, set count to null (don't show count)
        setDocumentCount(null)
      }
    } catch (e) {
      console.error('Error checking document count:', e)
      setDocumentCount(null) // Don't show count on error
    } finally {
      setIsCheckingCount(false)
    }
  }, [initialPath, hasDocumentPermission])

  // Fetch list of files when path changes (including empty path for root)
  // Only fetch immediately if in inline mode (no linkText)
  // If linkText is provided (modal mode), only fetch when dialog is open
  useEffect(() => {
    // If linkText is provided, only fetch when dialog is open
    if (linkText && !dialogOpen) {
      return // Don't fetch if modal is closed
    }
    // Allow empty path - it means root of bucket
    fetchFiles()
  }, [currentPath, fetchFiles, linkText, dialogOpen])

  // If linkText is provided, check document count on hover (not on mount to avoid overwhelming API)
  // This prevents making 50+ simultaneous API calls when component is used in a table
  // Only checks once per component instance (if documentCount is null)
  const handleLinkHover = useCallback(() => {
    if (linkText && initialPath && documentCount === null && !isCheckingCount) {
      checkDocumentCount()
    }
  }, [linkText, initialPath, documentCount, isCheckingCount, checkDocumentCount])

  // Handle folder navigation - only allow if path is at or below base path
  const handleFolderClick = (folderPath) => {
    if (isPathAllowed(folderPath)) {
      setCurrentPath(folderPath)
    } else {
      console.warn('Navigation blocked: Cannot navigate above base path', {
        requested: folderPath,
        basePath: basePath
      })
    }
  }

  // Handle breadcrumb navigation - only allow if path is at or below base path
  const handleBreadcrumbClick = (path) => {
    if (!basePath) {
      // No base path restriction
      setCurrentPath(path || '')
      return
    }
    
    // Normalize both paths for comparison
    const normalizedBase = basePath.endsWith('/') ? basePath : basePath + '/'
    const normalizedPath = path ? (path.endsWith('/') ? path : path + '/') : ''
    
    // If clicking on base path breadcrumb or empty, go to base
    if (!path || normalizedPath === normalizedBase) {
      setCurrentPath(basePath)
      return
    }
    
    // Check if path is allowed (at or below base)
    if (isPathAllowed(path)) {
      setCurrentPath(path)
    } else {
      console.warn('Breadcrumb navigation blocked: Cannot navigate above base path', {
        requested: path,
        normalizedRequested: normalizedPath,
        basePath: basePath,
        normalizedBase: normalizedBase
      })
    }
  }

  // Build breadcrumbs - only show base folder name and deeper folders (no parent structure)
  const buildBreadcrumbs = () => {
    const breadcrumbs = []
    
    // If there's a base path, only show the base folder name and anything deeper
    if (basePath) {
      // Normalize base path
      const normalizedBase = basePath.endsWith('/') ? basePath : basePath + '/'
      
      // Use the custom baseLabel (baseFolderLabel prop, title prop, or 'Documents' default)
      // Start breadcrumbs with the base folder label
      breadcrumbs.push({
        name: baseLabel,
        path: normalizedBase,
        isBasePath: true
      })
      
      // Add remaining path parts beyond base path (if we're deeper than base)
      if (currentPath && currentPath.startsWith(normalizedBase)) {
        const remainingPath = currentPath.substring(normalizedBase.length)
        const remainingParts = remainingPath.split('/').filter(p => p)
        let currentRemaining = normalizedBase
        
        remainingParts.forEach(part => {
          currentRemaining += part + '/'
          breadcrumbs.push({
            name: part,
            path: currentRemaining,
            isBasePath: false
          })
        })
      }
    } else {
      // No base path restriction - show full path from root
      breadcrumbs.push({ name: 'Root', path: '', isBasePath: false })
      
      if (currentPath) {
        const parts = currentPath.split('/').filter(p => p)
        let current = ''
        parts.forEach(part => {
          current += (current ? '/' : '') + part + '/'
          breadcrumbs.push({
            name: part,
            path: current,
            isBasePath: false
          })
        })
      }
    }
    
    return breadcrumbs
  }

  const downloadFile = async (file) => {
    // Use the actual S3 key for downloads (this is what's actually stored in S3)
    // The originalKey is only for access checks, but for S3 operations we need the real key
    const keyToUse = file.key // Always use the actual S3 key for downloads
    if (!keyToUse) return

    setDownloadingId(file.key)

    try {
      // For API Gateway {key+} path parameter, we need to encode each segment separately
      // Split by /, encode each part, then join with /
      const encodedKey = keyToUse.split('/').map(segment => encodeURIComponent(segment)).join('/')
      const apiUrl = `${api}/download-attachment/${encodedKey}`
      
      // Use ref to get current credentials without dependency
      const currentCredentials = credentialsRef.current
      const currentAccessToken = currentCredentials?.user?.accessToken || ''
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${currentAccessToken}`
        }
      })

      if (!response.ok) {
        // Try to parse error as JSON first
        let errorMessage = `Error downloading file: ${response.status}`
        if (response.status === 401) {
            props.credentials.login() 
            throw new Error('Your access token has expired. Please log in again.')
        } else
        try {
          const errorData = await response.json()
          if (errorData.error) {
            errorMessage = errorData.error
          }
        } catch (e) {
          // If JSON parsing fails, use status text
          errorMessage = response.statusText || errorMessage
        }
        throw new Error(errorMessage)
      }

      // Get presigned URL from response
      const result = await response.json()
      
      if (result.success && result.data && result.data.url) {
        // Use the presigned URL to download the file directly from S3
        const downloadUrl = result.data.url
        const filename = result.data.filename || file.name
        
        // Open/download the file using the presigned URL
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = filename
        link.target = '_blank' // Open in new tab as fallback
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } else {
        throw new Error('No download URL received from server')
      }
      
    } catch (e) {
      console.error('Error downloading file:', e)
      alert(`Error downloading file: ${e.message}`)
    } finally {
      setDownloadingId(null)
    }
  }

  // Combine folders and files, and transform data for table display
  // Folders come first, then files, all sorted alphabetically
  const foldersData = folders.map(folder => ({
    ...folder,
    name: folder.name,
    size: '-',
    lastModified: '-',
    download: null,
    type: 'folder'
  })).sort((a, b) => a.name.localeCompare(b.name))

  const filesData = files.map((file) => ({
    ...file,
    name: file.name,
    size: formatFileSize(file.size),
    lastModified: file.lastModified,
    download: downloadingId === file.key ? (
      <div style={{ paddingLeft: '1rem', display: 'flex', alignItems: 'center' }}>
        <CircularProgress
          className='loading-circle'
          disableShrink
          size={16}
        />
      </div>
    ) : (
      <div
        className='editLink'
        onClick={() => downloadFile(file)}
        style={{ cursor: 'pointer' }}
      >
        download
      </div>
    ),
    type: 'file'
  })).sort((a, b) => a.name.localeCompare(b.name))

  // Combine: folders first, then files
  const tableData = [...foldersData, ...filesData]

  const breadcrumbs = buildBreadcrumbs()

  // Render the document list content (breadcrumbs + table)
  const renderDocumentListContent = () => (
    <div style={{ padding: '1.5rem 0.5rem' }}>
      {/* {title && <h3 style={{ marginBottom: '1rem' }}>{title}</h3>}
       */}
      {/* Breadcrumb Navigation - Only shows paths at or below base path */}
      {breadcrumbs.length > 0 && (
        <Breadcrumbs
          aria-label="breadcrumb"
          separator={<NavigateNextIcon fontSize="small" />}
          style={{ marginBottom: '1rem' }}
        >
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1
            const isBaseCrumb = crumb.isBasePath
            
            // Last breadcrumb is not clickable (you're already there)
            if (isLast) {
              return (
                <Typography key={crumb.path || 'root'} color="textPrimary">
                  {crumb.name}
                </Typography>
              )
            }
            
            // All other breadcrumbs are clickable (they're all at or below base path)
            // This includes the base path breadcrumb when you're in a deeper folder
            return (
              <Link
                key={crumb.path || 'root'}
                color="inherit"
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  handleBreadcrumbClick(crumb.path)
                }}
                style={{ 
                  cursor: 'pointer',
                  fontWeight: isBaseCrumb ? 600 : 400
                }}
              >
                {crumb.name}
              </Link>
            )
          })}
        </Breadcrumbs>
      )}
      
      {error && (
        <div style={{ 
          color: '#d32f2f', 
          marginBottom: '1rem',
          padding: '1rem',
          backgroundColor: '#ffebee',
          borderRadius: '4px',
          fontWeight: 500
        }}>
          ⚠️ {error}
        </div>
      )}
      
      <ReactTable
        isLoading={isLoading}
        cols={getDocumentListCols(handleFolderClick)}
        data={tableData}
        tableKey={`document-list-${currentPath}`}
      />
      
      {!isLoading && folders.length === 0 && files.length === 0 && !error && (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
          No folders or files found in this location
        </div>
      )}
    </div>
  )

  // If linkText is provided, render as clickable link that opens modal
  // Only render if DOCUMENTS module is enabled
  if (linkText) {
    // If DOCUMENTS module is not enabled, don't render the link
    if (!isDocumentsModuleEnabled || !hasDocumentPermission) {
      return null
    }
    
    return (
      <>
        <div
          href="#"
          onClick={(e) => {
            e.preventDefault()
            setDialogOpen(true)
          }}
          className='editLink'
          onMouseEnter={(e) => {
            e.currentTarget.style.textDecoration = 'underline'
            handleLinkHover() // Check count on hover instead of on mount
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.textDecoration = 'none'
          }}
        >
          {linkText}
          {isCheckingCount && (
            <span style={{ marginLeft: '8px', fontSize: '0.9em', color: '#666' }}>
              (checking...)
            </span>
          )}
          {!isCheckingCount && documentCount !== null && (
            <span style={{ marginLeft: '8px', fontSize: '0.9em', color: '#666' }}>
              ({documentCount.folders + documentCount.files > 0 
                ? `${documentCount.folders + documentCount.files} item${documentCount.folders + documentCount.files !== 1 ? 's' : ''}`
                : 'empty'})
            </span>
          )}
        </div>

        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            {title || baseLabel || 'Documents'}
            <IconButton
              aria-label="close"
              onClick={() => setDialogOpen(false)}
              style={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: '#999'
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers style={{ overflow: 'auto', padding: 0 }}>
            {renderDocumentListContent()}
          </DialogContent>
        </Dialog>
      </>
    )
  }

  // Otherwise, render inline (original behavior)
  // Only render if DOCUMENTS module is enabled
  if (!isDocumentsModuleEnabled || !hasDocumentPermission) {
    return null
  }
  
  return renderDocumentListContent()
}

